import { useCallback, useMemo, useState } from "react";

// BK-3229 R4 — Compose tab selectors that the Library-tab auto-population
// drives. Used as state keys on `templateFlags` and as the `field` arg the
// selector's manual-change wrappers pass up. Keeping the name in one place
// prevents "ModelTier" / "model" / "MODEL_TIER" drift across the codebase.
const FLAG_KEYS = Object.freeze({
  MODEL: "model",
  CONTEXT: "context",
  FORMAT: "format",
});

const INITIAL_FLAGS = Object.freeze({
  [FLAG_KEYS.MODEL]: false,
  [FLAG_KEYS.CONTEXT]: false,
  [FLAG_KEYS.FORMAT]: false,
});

// Category rationale copy shown in the per-selector template-set tooltip.
// Kept alongside the hook so the ModalContent payload and the indicator
// tooltip agree on the string.
const buildActiveTemplate = (prompt) => ({
  id: prompt.id,
  title: prompt.title,
  rationale: prompt.category?.name
    ? `Recommended for ${prompt.category.name} templates.`
    : null,
});

// Clearing a per-field flag only when it's currently true prevents a
// no-op state update from triggering an extra render.
const clearFlagIfSet = (prev, field) =>
  prev[field] ? { ...prev, [field]: false } : prev;

// Encapsulates the R4 auto-population state — per-field template flags, the
// active-template tooltip payload, and the three "manual override clears the
// icon" selector wrappers. ModalContent becomes a pure orchestrator; this
// hook owns the state machine.
const useTemplateAutoPopulation = ({
  onTierChange,
  onContextDepthChange,
  onOutputFormatChange,
}) => {
  const [templateFlags, setTemplateFlags] = useState(INITIAL_FLAGS);
  const [activeTemplate, setActiveTemplate] = useState(null);

  const applyTemplateDefaults = useCallback(
    (prompt) => {
      const templateTier = prompt.default_model_tier;
      const templateDepth = prompt.default_context_depth;
      const templateFormat = prompt.default_format;

      if (templateTier) {
        onTierChange(templateTier);
      }
      if (templateDepth) {
        onContextDepthChange(templateDepth);
      }
      if (templateFormat) {
        onOutputFormatChange(templateFormat);
      }

      setTemplateFlags({
        [FLAG_KEYS.MODEL]: !!templateTier,
        [FLAG_KEYS.CONTEXT]: !!templateDepth,
        [FLAG_KEYS.FORMAT]: !!templateFormat,
      });
      setActiveTemplate(buildActiveTemplate(prompt));
    },
    [onTierChange, onContextDepthChange, onOutputFormatChange],
  );

  const handleManualTierChange = useCallback(
    (value) => {
      setTemplateFlags((prev) => clearFlagIfSet(prev, FLAG_KEYS.MODEL));
      onTierChange(value);
    },
    [onTierChange],
  );

  const handleManualContextDepthChange = useCallback(
    (value) => {
      setTemplateFlags((prev) => clearFlagIfSet(prev, FLAG_KEYS.CONTEXT));
      onContextDepthChange(value);
    },
    [onContextDepthChange],
  );

  const handleManualOutputFormatChange = useCallback(
    (value) => {
      setTemplateFlags((prev) => clearFlagIfSet(prev, FLAG_KEYS.FORMAT));
      onOutputFormatChange(value);
    },
    [onOutputFormatChange],
  );

  // One stable handle for the consumer so deps arrays stay short.
  const manualChangeHandlers = useMemo(
    () => ({
      tier: handleManualTierChange,
      contextDepth: handleManualContextDepthChange,
      outputFormat: handleManualOutputFormatChange,
    }),
    [
      handleManualTierChange,
      handleManualContextDepthChange,
      handleManualOutputFormatChange,
    ],
  );

  return {
    templateFlags,
    activeTemplate,
    applyTemplateDefaults,
    manualChangeHandlers,
  };
};

export default useTemplateAutoPopulation;
export { FLAG_KEYS };
